import React, { useState, useMemo, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  CircularProgress,
  LinearProgress,
  Typography,
  Chip,
  Stack,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Icon } from "@iconify/react";
import { useDispatch } from "react-redux";
import { setDialog } from "../../features/dialog/dialogSlice";
import { useBookEventMutation } from "../../features/eventRegistrations/eventRegistrationsApiSlice";
import BookingStepParticipants from "./booking/BookingStepParticipants";
import BookingStepSessions from "./booking/BookingStepSessions";
import BookingStepCustomFields from "./booking/BookingStepCustomFields";
import BookingStepPayment from "./booking/BookingStepPayment";
import BookingStepSummary from "./booking/BookingStepSummary";
import { calculateBookingPricePreview } from "./booking/bookingPricingUtils";

const ERROR_MESSAGES = {
  409: "Sie haben dieses Event bereits gebucht.",
  423: "Leider sind keine Plätze mehr verfügbar. Sie wurden auf die Warteliste gesetzt.",
};

const EMPTY_CUSTOM_FIELD_ERRORS = { booking: {}, participants: {} };

const isMinor = (birthdate) => {
  if (!birthdate) return false;
  const age =
    (new Date() - new Date(birthdate)) / (1000 * 60 * 60 * 24 * 365.25);
  return age < 18;
};

const isMissingRequiredValue = (field, value) => {
  if (!field?.required) return false;

  if (field.type === "checkbox") {
    return value !== true;
  }

  if (field.type === "multiselect") {
    return !Array.isArray(value) || value.length === 0;
  }

  if (typeof value === "string") {
    return value.trim() === "";
  }

  return value === undefined || value === null || value === "";
};

const getParticipantFieldKey = (participant, idx) =>
  String(participant?._key || participant?.memberRef || idx);

const getPaymentSuccessText = (
  paymentInfo = {},
  fallbackPaymentMethod = "",
) => {
  const method = String(paymentInfo?.method || fallbackPaymentMethod || "")
    .trim()
    .toLowerCase();
  if (method === "not_applicable") {
    return "Für diese Anmeldung ist keine Zahlung erforderlich.";
  }
  if (method === "sepa") {
    return "Die Gebühr wird vom Inhaber des Vertrags per SEPA-Lastschrift eingezogen.";
  }
  if (method === "bank_transfer") {
    return "Die Überweisungsdaten finden Sie in der Buchungszusammenfassung und in Ihrer Bestätigungs-E-Mail.";
  }
  return "";
};

export default function EventBookingDialog({
  open,
  onClose,
  event,
  onSuccess,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const [bookEvent, { isLoading }] = useBookEventMutation();
  const contentRef = useRef(null);

  const hasSessions = (event?.sessions?.length || 0) > 0;
  const hasPricingRules = (event?.pricingRules?.length || 0) > 0;
  const defaultPricingRule =
    (event?.pricingRules || []).find((r) => r.isDefault) ||
    event?.pricingRules?.[0];
  const [bookingPreviewDate, setBookingPreviewDate] = useState(
    () => new Date(),
  );
  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentStepError, setPaymentStepError] = useState("");

  useEffect(() => {
    if (!open) return;
    setBookingPreviewDate(new Date());
    setPaymentMethod("");
    setPaymentStepError("");
  }, [event?._id, open]);

  const eligibleMembers = useMemo(() => {
    const preferredMembers = Array.isArray(event?.participantEligibleMembers)
      ? event.participantEligibleMembers
      : [];
    const fallbackMembers = Array.isArray(event?.contractMembers)
      ? event.contractMembers
      : [];
    const sourceMembers =
      preferredMembers.length > 0 ? preferredMembers : fallbackMembers;

    return sourceMembers
      .map((member) => ({
        _id: String(member?._id || "").trim(),
        firstName: String(member?.firstName || "").trim(),
        lastName: String(member?.lastName || "").trim(),
        birthdate: member?.birthdate || undefined,
        memberNumber: String(member?.memberNumber || "").trim(),
      }))
      .filter((member) => Boolean(member._id));
  }, [event]);

  const [activeStep, setActiveStep] = useState(0);
  const [participants, setParticipants] = useState([]);
  const [selectedSessionIds, setSelectedSessionIds] = useState([]);
  const [selectedPricingRuleId, setSelectedPricingRuleId] = useState(
    defaultPricingRule?.ruleId || "",
  );
  const [bookingCustomFieldValues, setBookingCustomFieldValues] = useState({});
  const [participantCustomFieldValues, setParticipantCustomFieldValues] =
    useState({});
  const [consents, setConsents] = useState([]);
  const [missingConsentIds, setMissingConsentIds] = useState([]);
  const [submitError, setSubmitError] = useState("");
  const [customFieldErrors, setCustomFieldErrors] = useState(
    EMPTY_CUSTOM_FIELD_ERRORS,
  );
  const [customFieldsStepError, setCustomFieldsStepError] = useState("");
  const [customFieldFocusTarget, setCustomFieldFocusTarget] = useState(null);

  const clearCustomFieldError = ({ scope, participantKey, fieldId }) => {
    setCustomFieldErrors((prev) => {
      if (!prev) return EMPTY_CUSTOM_FIELD_ERRORS;
      if (scope === "booking") {
        if (!prev.booking?.[fieldId]) return prev;
        const nextBooking = { ...(prev.booking || {}) };
        delete nextBooking[fieldId];
        return { ...prev, booking: nextBooking };
      }

      const pKey = String(participantKey || "");
      if (!prev.participants?.[pKey]?.[fieldId]) return prev;
      const nextParticipantFields = { ...(prev.participants[pKey] || {}) };
      delete nextParticipantFields[fieldId];
      const nextParticipants = { ...(prev.participants || {}) };
      if (Object.keys(nextParticipantFields).length === 0) {
        delete nextParticipants[pKey];
      } else {
        nextParticipants[pKey] = nextParticipantFields;
      }
      return { ...prev, participants: nextParticipants };
    });
  };

  const validateCustomFieldsStep = () => {
    const customFields = event?.registrationConfig?.customFields || [];
    const bookingFields = customFields.filter(
      (field) => field.scope === "booking",
    );
    const participantFields = customFields.filter(
      (field) => field.scope === "participant",
    );

    const nextErrors = { booking: {}, participants: {} };
    let firstInvalid = null;

    bookingFields.forEach((field) => {
      const value = bookingCustomFieldValues?.[field.fieldId];
      if (isMissingRequiredValue(field, value)) {
        nextErrors.booking[field.fieldId] = "Dieses Feld ist erforderlich.";
        if (!firstInvalid) {
          firstInvalid = { scope: "booking", fieldId: field.fieldId };
        }
      }
    });

    participants.forEach((participant, idx) => {
      const participantKey = getParticipantFieldKey(participant, idx);
      const participantValues =
        participantCustomFieldValues?.[participantKey] || {};
      const minor = isMinor(participant?.birthdate);

      participantFields.forEach((field) => {
        if (field.onlyForChildren && !minor) return;
        const value = participantValues[field.fieldId];
        if (!isMissingRequiredValue(field, value)) return;

        if (!nextErrors.participants[participantKey]) {
          nextErrors.participants[participantKey] = {};
        }
        nextErrors.participants[participantKey][field.fieldId] =
          "Dieses Feld ist erforderlich.";

        if (!firstInvalid) {
          firstInvalid = {
            scope: "participant",
            participantKey,
            fieldId: field.fieldId,
          };
        }
      });
    });

    const hasErrors =
      Object.keys(nextErrors.booking).length > 0 ||
      Object.keys(nextErrors.participants).length > 0;

    return { hasErrors, nextErrors, firstInvalid };
  };

  const handleBookingFieldChange = (fieldId, value) => {
    setBookingCustomFieldValues((prev) => ({ ...prev, [fieldId]: value }));
    clearCustomFieldError({ scope: "booking", fieldId });
    setCustomFieldsStepError("");
    setCustomFieldFocusTarget(null);
  };

  const handleParticipantFieldChange = (participantKey, fieldId, value) => {
    setParticipantCustomFieldValues((prev) => ({
      ...prev,
      [participantKey]: { ...(prev[participantKey] || {}), [fieldId]: value },
    }));
    clearCustomFieldError({ scope: "participant", participantKey, fieldId });
    setCustomFieldsStepError("");
    setCustomFieldFocusTarget(null);
  };

  const handleConsentsChange = (nextConsents) => {
    setConsents(nextConsents);

    if (missingConsentIds.length === 0) return;
    const nextConsentIds = new Set(
      (nextConsents || []).map((c) => c.consentId),
    );
    const stillMissing = missingConsentIds.filter(
      (cid) => !nextConsentIds.has(cid),
    );
    setMissingConsentIds(stillMissing);
    if (
      stillMissing.length === 0 &&
      submitError ===
        "Bitte bestätigen Sie alle Pflicht-Einwilligungen, bevor Sie die Anmeldung absenden."
    ) {
      setSubmitError("");
    }
  };

  const memberParticipantCount = participants.reduce(
    (count, participant) => count + (participant?.memberRef ? 1 : 0),
    0,
  );
  const paymentPreview = useMemo(
    () =>
      calculateBookingPricePreview({
        event,
        selectedPricingRuleId,
        selectedSessionIds,
        participantCount: participants.length,
        memberParticipantCount,
        bookingDate: bookingPreviewDate,
      }),
    [
      bookingPreviewDate,
      event,
      memberParticipantCount,
      participants.length,
      selectedPricingRuleId,
      selectedSessionIds,
    ],
  );
  const totalAmount = Number(paymentPreview?.totalAmount || 0);
  const paymentRequired = totalAmount > 0;

  const STEPS = useMemo(() => {
    const s = ["Teilnehmer"];
    if (hasSessions || hasPricingRules) s.push("Sessions & Preis");
    s.push("Angaben");
    if (paymentRequired) s.push("Zahlung");
    s.push("Zusammenfassung");
    return s;
  }, [hasPricingRules, hasSessions, paymentRequired]);

  useEffect(() => {
    setActiveStep((prev) => Math.min(prev, Math.max(0, STEPS.length - 1)));
  }, [STEPS.length]);

  useEffect(() => {
    if (paymentRequired) return;
    setPaymentMethod("");
    setPaymentStepError("");
  }, [paymentRequired]);

  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeStep]);

  const handleSubmit = async () => {
    setSubmitError("");
    setMissingConsentIds([]);

    if (paymentRequired && !paymentMethod) {
      const errorText =
        "Bitte wählen Sie eine Zahlungsart, um die Anmeldung abzuschließen.";
      setPaymentStepError(errorText);
      setSubmitError(errorText);
      const paymentStepIndex = STEPS.indexOf("Zahlung");
      if (paymentStepIndex >= 0) {
        setActiveStep(paymentStepIndex);
      }
      return;
    }

    const requiredConsentIds = (
      event?.registrationConfig?.requiredConsents || []
    )
      .filter((consent) => consent?.required !== false)
      .map((consent) => consent.consentId)
      .filter(Boolean);
    const consentIdSet = new Set(
      (consents || []).map((consent) => consent.consentId),
    );
    const missingRequiredConsents = requiredConsentIds.filter(
      (cid) => !consentIdSet.has(cid),
    );

    if (missingRequiredConsents.length > 0) {
      setMissingConsentIds(missingRequiredConsents);
      setSubmitError(
        "Bitte bestätigen Sie alle Pflicht-Einwilligungen, bevor Sie die Anmeldung absenden.",
      );
      contentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    try {
      // Teilnehmer bereinigen (interne _key-Property entfernen)
      const cleanParticipants = participants.map((participant, idx) => {
        const participantKey = getParticipantFieldKey(participant, idx);
        const { _key, ...p } = participant;
        const participantValues =
          participantCustomFieldValues[participantKey] || {};

        return {
          ...p,
          // Gesundheitsdaten aus participantCustomFieldValues herausziehen
          healthData: {
            allergies: participantValues._healthAllergies || "",
            medications: participantValues._healthMedications || "",
            dietaryNeeds: participantValues._healthDietary || "",
            emergencyContact: {
              name: participantValues._emergencyName || "",
              phone: participantValues._emergencyPhone || "",
              relationship: participantValues._emergencyRelation || "",
            },
          },
          // Reguläre custom fields (ohne health-Prefix)
          customFieldValues: Object.fromEntries(
            Object.entries(participantValues).filter(
              ([k]) => !k.startsWith("_health") && !k.startsWith("_emergency"),
            ),
          ),
        };
      });

      const result = await bookEvent({
        eventId: event._id,
        participants: cleanParticipants,
        selectedSessionIds,
        selectedPricingRuleId: selectedPricingRuleId || undefined,
        customFieldValues: bookingCustomFieldValues,
        consents,
        paymentMethod: paymentRequired ? paymentMethod : undefined,
      }).unwrap();

      const successSegments = [
        result?.message || "Ihre Anmeldung wurde eingereicht.",
        getPaymentSuccessText(
          result?.paymentInfo,
          result?.paymentMethod || paymentMethod,
        ),
      ].filter(Boolean);

      dispatch(
        setDialog({
          type: "SUCCESS",
          title: "Anmeldung erfolgreich",
          data: { successText: successSegments.join("\n\n") },
        }),
      );
      onSuccess?.();
    } catch (e) {
      const status = e?.status;
      const msg =
        ERROR_MESSAGES[status] ||
        e?.data?.message ||
        "Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.";

      if (status === 423) {
        // Warteliste — eigentlich kein Fehler, aber wir zeigen Info
        dispatch(
          setDialog({
            type: "SUCCESS",
            title: "Warteliste",
            data: { successText: msg },
          }),
        );
        onSuccess?.();
      } else {
        setSubmitError(msg);
      }
    }
  };

  const customFieldsStepIndex = STEPS.indexOf("Angaben");
  const paymentStepIndex = STEPS.indexOf("Zahlung");

  const renderStep = () => {
    // Schritt-Mapping je nach ob Sessions vorhanden
    const stepNames = STEPS;
    const currentStepName = stepNames[activeStep];

    if (currentStepName === "Teilnehmer") {
      return (
        <BookingStepParticipants
          eligibleMembers={eligibleMembers}
          participants={participants}
          onChange={setParticipants}
        />
      );
    }
    if (currentStepName === "Sessions & Preis") {
      return (
        <BookingStepSessions
          event={event}
          selectedSessionIds={selectedSessionIds}
          selectedPricingRuleId={selectedPricingRuleId}
          participantCount={participants.length}
          memberParticipantCount={memberParticipantCount}
          bookingDate={bookingPreviewDate}
          onSessionsChange={setSelectedSessionIds}
          onPricingRuleChange={setSelectedPricingRuleId}
        />
      );
    }
    if (currentStepName === "Angaben") {
      return (
        <BookingStepCustomFields
          event={event}
          participants={participants}
          bookingCustomFieldValues={bookingCustomFieldValues}
          participantCustomFieldValues={participantCustomFieldValues}
          onBookingFieldChange={handleBookingFieldChange}
          onParticipantFieldChange={handleParticipantFieldChange}
          validationErrors={customFieldErrors}
          focusField={customFieldFocusTarget}
          stepError={customFieldsStepError}
        />
      );
    }
    if (currentStepName === "Zahlung") {
      return (
        <BookingStepPayment
          event={event}
          paymentMethod={paymentMethod}
          onPaymentMethodChange={(value) => {
            setPaymentMethod(value);
            setPaymentStepError("");
            setSubmitError("");
          }}
          paymentError={paymentStepError}
          totalAmount={totalAmount}
          paymentRequired={paymentRequired}
        />
      );
    }
    if (currentStepName === "Zusammenfassung") {
      return (
        <BookingStepSummary
          event={event}
          participants={participants}
          selectedSessionIds={selectedSessionIds}
          selectedPricingRuleId={selectedPricingRuleId}
          memberParticipantCount={memberParticipantCount}
          bookingDate={bookingPreviewDate}
          consents={consents}
          onConsentsChange={handleConsentsChange}
          missingConsentIds={missingConsentIds}
          submitError={submitError}
          paymentMethod={paymentMethod}
        />
      );
    }
    return null;
  };

  const handleNext = () => {
    if (activeStep === customFieldsStepIndex) {
      const { hasErrors, nextErrors, firstInvalid } =
        validateCustomFieldsStep();
      if (hasErrors) {
        setCustomFieldErrors(nextErrors);
        setCustomFieldsStepError(
          "Bitte füllen Sie alle als Pflichtfeld markierten Angaben aus.",
        );
        setCustomFieldFocusTarget(firstInvalid);
        contentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }

      setCustomFieldErrors(EMPTY_CUSTOM_FIELD_ERRORS);
      setCustomFieldsStepError("");
      setCustomFieldFocusTarget(null);
    }

    if (activeStep === paymentStepIndex && paymentRequired && !paymentMethod) {
      setPaymentStepError("Bitte wählen Sie eine Zahlungsart.");
      contentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setPaymentStepError("");
    setActiveStep((s) => s + 1);
  };

  const handleBack = () => setActiveStep((s) => s - 1);

  const canProceed = () => {
    // Schritt 0: Teilnehmer
    if (activeStep === 0) return participants.length > 0;
    if (activeStep === paymentStepIndex && paymentRequired) {
      return Boolean(paymentMethod);
    }
    // Alle anderen Schritte: immer erlaubt (Validierung beim Submit)
    return true;
  };

  const isLastStep = activeStep === STEPS.length - 1;

  const progressPercent = ((activeStep + 1) / STEPS.length) * 100;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle sx={{ px: isMobile ? 2 : 3, py: isMobile ? 1.5 : 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant={isMobile ? "subtitle1" : "h6"}
              noWrap
              sx={{ fontWeight: 600 }}
            >
              {event?.eventName}
            </Typography>
            <Chip
              label="Buchungs-Event"
              color="primary"
              size="small"
              sx={{ mt: 0.5 }}
            />
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ ml: 1 }}>
            <Icon icon="mdi:close" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent ref={contentRef} dividers sx={{ px: isMobile ? 2 : 3 }}>
        {/* Mobile: compact progress bar + step chips */}
        {isMobile ? (
          <Stack spacing={1} sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                size="small"
                color="primary"
                label={`${activeStep + 1}/${STEPS.length}`}
              />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {STEPS[activeStep]}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={progressPercent}
              sx={{ height: 6, borderRadius: 999 }}
            />
          </Stack>
        ) : (
          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}

        <Box sx={{ minHeight: isMobile ? 200 : 280 }}>{renderStep()}</Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: isMobile ? 2 : 3,
          py: isMobile ? 1.5 : 2,
          flexDirection: isMobile ? "column-reverse" : "row",
          gap: isMobile ? 1 : 0,
        }}
      >
        <Button
          onClick={activeStep === 0 ? onClose : handleBack}
          disabled={isLoading}
          fullWidth={isMobile}
          startIcon={
            activeStep > 0 ? <Icon icon="mdi:arrow-left" width={16} /> : null
          }
        >
          {activeStep === 0 ? "Abbrechen" : "Zurück"}
        </Button>
        {!isMobile && <Box flex={1} />}
        {isLastStep ? (
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={isLoading}
            fullWidth={isMobile}
            startIcon={
              isLoading ? (
                <CircularProgress size={16} />
              ) : (
                <Icon icon="mdi:check" />
              )
            }
          >
            Kostenpflichtig anmelden
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={!canProceed()}
            fullWidth={isMobile}
            endIcon={<Icon icon="mdi:arrow-right" />}
          >
            Weiter
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

EventBookingDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  event: PropTypes.object.isRequired,
  onSuccess: PropTypes.func,
};
